import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ShareButtons from "../components/ShareButtons";
import FollowButton from "../components/FollowButton";
import { ArrowLeft, Play, Pause, Disc, Download, RotateCcw, RotateCw, Share2, Heart } from "lucide-react";
import { stopAllMedia, registerStopCallback } from "../hooks/useAudioPlayer";
import { isLiked, toggleLike } from "../lib/userPrefs";
import { slugifyArtist } from "../lib/slug";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DVDDetailPage() {
  const { id } = useParams();
  const [dvd, setDvd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingIdx, setPlayingIdx] = useState(null);
  const [liked, setLiked] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchDvd = async () => {
      try {
        const res = await axios.get(`${API}/dvds/${id}`);
        setDvd(res.data);
        setLiked(isLiked("dvds", res.data.id));
      } catch (e) {
        console.error("Erro ao buscar DVD:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDvd();
  }, [id]);

  // Register this video as stoppable by global audio player
  useEffect(() => {
    const unregister = registerStopCallback(() => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setPlayingIdx(null);
    });
    return unregister;
  }, []);

  const handlePlayVideo = useCallback((idx) => {
    if (playingIdx === idx) {
      // Pause current video
      if (videoRef.current) videoRef.current.pause();
      setPlayingIdx(null);
    } else {
      // Stop ALL other media (audio from playlists/ranking)
      stopAllMedia();
      setPlayingIdx(idx);
    }
  }, [playingIdx]);

  const downloadVideo = (e, idx, title) => {
    e.stopPropagation();
    const url = `${API}/dvds/${id}/video/${idx}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Baixando "${title}"...`);
  };

  const [zipping, setZipping] = useState(false);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoPaused, setVideoPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragTime, setDragTime] = useState(null);

  const seekVideo = (seconds) => {
    if (videoRef.current && Number.isFinite(seconds)) {
      videoRef.current.currentTime = Math.max(0, Math.min(videoRef.current.duration || 0, seconds));
    }
  };
  const skip = (delta) => {
    if (videoRef.current) seekVideo((videoRef.current.currentTime || 0) + delta);
  };
  const togglePauseVideo = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setVideoPaused(false);
    } else {
      videoRef.current.pause();
      setVideoPaused(true);
    }
  };
  const fmtTime = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec < 10 ? "0" : ""}${sec}`;
  };

  // === Drag-to-seek for video ===
  const seekFromX = (clientX, barEl) => {
    const rect = barEl.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * (videoRef.current?.duration || 0);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const bar = document.querySelector('[data-active-dvd-seek="1"]');
      if (bar) {
        const t = seekFromX(cx, bar);
        setDragTime(t);
      }
    };
    const onUp = (e) => {
      const cx = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : e.clientX;
      const bar = document.querySelector('[data-active-dvd-seek="1"]');
      if (bar && cx !== undefined && cx !== null) {
        seekVideo(seekFromX(cx, bar));
      } else if (dragTime !== null) {
        seekVideo(dragTime);
      }
      setDragging(false);
      setDragTime(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, dragTime]);

  const startDrag = (e, barEl) => {
    setDragging(true);
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    setDragTime(seekFromX(cx, barEl));
  };

  const shareVideo = async (idx, video) => {
    const url = `${window.location.origin}/dvd/${id}?v=${idx}`;
    const data = {
      title: `${dvd.artist_name} - ${video.title}`,
      text: `Assista "${video.title}" no Arrocha Play`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado!");
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado!");
      } catch {
        toast.error("Erro ao compartilhar");
      }
    }
  };

  const downloadAll = () => {
    if (zipping) return;
    if (!dvd?.videos?.length) {
      toast.error("DVD sem vídeos");
      return;
    }
    setZipping(true);
    toast.info(`Preparando ZIP com ${dvd.videos.length} vídeo(s)...`);
    const url = `${API}/dvds/${id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => setZipping(false), 8000);
  };

  if (loading || !dvd) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <main className="pt-24 max-w-7xl mx-auto px-4">
          <div className="animate-pulse text-center text-zinc-500 py-20">
            {loading ? "Carregando..." : "DVD não encontrado"}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <main className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6" data-testid="dvd-detail-page">
        <Link to="/dvds" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} />
          Voltar para DVDs
        </Link>

        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {dvd.cover_image && (
            <div className="w-full md:w-64 flex-shrink-0">
              <img
                src={dvd.cover_image}
                alt={dvd.title}
                className="w-full rounded-xl object-contain bg-[#0a0a0a]"
                style={{ maxHeight: "350px" }}
              />
            </div>
          )}
          <div className="flex flex-col justify-end">
            <span className="bg-[#FF007F] text-white text-xs font-bold px-3 py-1 rounded-full w-fit mb-3">DVD</span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">{dvd.title}</h1>
            <p className="text-zinc-400 text-lg mb-2">
              <Link to={`/artista/${slugifyArtist(dvd.artist_name)}`} className="hover:text-[#FF007F] transition-colors">
                {dvd.artist_name}
              </Link>
            </p>
            <div className="mb-3">
              <FollowButton artistName={dvd.artist_name} slug={slugifyArtist(dvd.artist_name)} size="md" />
            </div>
            {dvd.description && <p className="text-zinc-500 text-sm">{dvd.description}</p>}
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <button
                onClick={() => {
                  if (!dvd) return;
                  const now = toggleLike("dvds", dvd.id);
                  setLiked(now);
                  toast.success(now ? "Adicionado aos favoritos" : "Removido dos favoritos");
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold transition-all border ${
                  liked
                    ? "bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/60"
                    : "bg-white/5 text-zinc-300 hover:text-[#FF007F] hover:border-[#FF007F]/40 border-white/10"
                }`}
                data-testid="like-dvd-btn"
              >
                <Heart size={16} fill={liked ? "#FF007F" : "none"} />
                {liked ? "Curtido" : "Curtir"}
              </button>
              <button
                onClick={downloadAll}
                disabled={zipping || !dvd.videos?.length}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait text-black font-bold rounded-full transition-all shadow-lg shadow-emerald-500/25"
                data-testid="download-dvd-btn"
                title="Baixa todos os vídeos do DVD em um único ZIP"
              >
                <Download size={16} />
                {zipping ? "Preparando..." : "Baixar DVD"}
              </button>
              <ShareButtons url={`/dvd/${dvd.id}`} title={dvd.title} size="md" />
            </div>
            <p className="text-zinc-600 text-xs mt-3">{dvd.videos?.length || 0} vídeo(s)</p>
          </div>
        </div>

        <div className="space-y-4">
          {dvd.videos?.map((video, idx) => (
            <div key={video.id || idx} className="bg-[#121212] rounded-xl overflow-hidden border border-white/10">
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Disc className="text-[#FF007F]" size={20} />
                  <div>
                    <h3 className="text-white font-medium">{video.title}</h3>
                    {video.duration && <span className="text-zinc-500 text-xs">{video.duration}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => downloadVideo(e, idx, video.title)}
                    className="w-10 h-10 rounded-full text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 flex items-center justify-center transition-all"
                    title="Baixar este vídeo"
                    data-testid={`download-video-${idx}`}
                  >
                    <Download size={18} />
                  </button>
                  <button
                    onClick={() => handlePlayVideo(idx)}
                    className="w-10 h-10 rounded-full bg-[#FF007F] flex items-center justify-center hover:bg-[#FF3399] transition-colors"
                    data-testid={`dvd-play-${idx}`}
                  >
                    {playingIdx === idx ? (
                      <Pause size={18} fill="white" className="text-white" />
                    ) : (
                      <Play size={18} fill="white" className="text-white ml-0.5" />
                    )}
                  </button>
                </div>
              </div>
              {playingIdx === idx && (
                <div className="w-full bg-black">
                  <video
                    ref={videoRef}
                    src={video.video_url}
                    controls
                    autoPlay
                    onTimeUpdate={(e) => setVideoTime(e.currentTarget.currentTime || 0)}
                    onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration || 0)}
                    onPlay={() => setVideoPaused(false)}
                    onPause={() => setVideoPaused(true)}
                    className="w-full max-h-[70vh]"
                    style={{ aspectRatio: "16/9" }}
                    data-testid={`dvd-video-${idx}`}
                  >
                    Seu navegador não suporta vídeo.
                  </video>
                  {/* Custom seek + skip controls (always visible) */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-[#0A0A0A] border-t border-white/5">
                    <button
                      onClick={() => skip(-10)}
                      className="w-9 h-9 rounded-full text-zinc-300 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
                      title="Voltar 10 segundos"
                      data-testid={`dvd-skip-back-${idx}`}
                    >
                      <RotateCcw size={16} />
                    </button>
                    <button
                      onClick={togglePauseVideo}
                      className="w-10 h-10 rounded-full bg-[#FF007F] hover:bg-[#FF3399] text-white flex items-center justify-center transition-all"
                      title={videoPaused ? "Tocar" : "Pausar"}
                      data-testid={`dvd-toggle-${idx}`}
                    >
                      {videoPaused ? <Play size={18} fill="white" className="ml-0.5"/> : <Pause size={18} fill="white"/>}
                    </button>
                    <button
                      onClick={() => skip(10)}
                      className="w-9 h-9 rounded-full text-zinc-300 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
                      title="Avançar 10 segundos"
                      data-testid={`dvd-skip-fwd-${idx}`}
                    >
                      <RotateCw size={16} />
                    </button>
                    <button
                      onClick={() => shareVideo(idx, video)}
                      className="w-9 h-9 rounded-full text-zinc-300 hover:text-emerald-400 hover:bg-emerald-500/10 flex items-center justify-center transition-all"
                      title="Compartilhar este vídeo"
                      data-testid={`dvd-share-${idx}`}
                    >
                      <Share2 size={15} />
                    </button>
                    <span className="text-xs text-zinc-400 tabular-nums w-12 text-right">
                      {fmtTime(dragging && dragTime !== null ? dragTime : videoTime)}
                    </span>
                    <div
                      onMouseDown={(e) => startDrag(e, e.currentTarget)}
                      onTouchStart={(e) => startDrag(e, e.currentTarget)}
                      className="flex-1 h-2 bg-white/10 rounded-full cursor-pointer group relative select-none"
                      role="slider"
                      aria-valuemin={0}
                      aria-valuemax={Math.floor(videoDuration) || 1}
                      aria-valuenow={Math.floor(dragging && dragTime !== null ? dragTime : videoTime)}
                      data-testid={`dvd-seek-${idx}`}
                      data-active-dvd-seek={dragging ? "1" : undefined}
                    >
                      {(() => {
                        const t = dragging && dragTime !== null ? dragTime : videoTime;
                        const pct = videoDuration > 0 ? (t / videoDuration) * 100 : 0;
                        return (
                          <>
                            <div
                              className="absolute inset-y-0 left-0 bg-[#FF007F] rounded-full pointer-events-none"
                              style={{ width: `${pct}%`, transition: dragging ? "none" : "width 0.1s linear" }}
                            />
                            <div
                              className={`absolute top-1/2 w-4 h-4 bg-[#FF007F] rounded-full shadow-md pointer-events-none transition-opacity ${
                                dragging ? "opacity-100 scale-125" : "opacity-0 group-hover:opacity-100"
                              }`}
                              style={{ left: `calc(${pct}% - 8px)`, transform: "translateY(-50%)" }}
                            />
                          </>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-zinc-400 tabular-nums w-12">{fmtTime(videoDuration)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
